// Helper module for manage_event methods moved out of the Vue component
// Each function accepts the Vue component instance (vm) as the first arg

export function add_ticket(vm) {
  vm.tickets.push({ name: '', price: 0, quantity: 0, end_date: '', start_date: '' });
}

function check_duration(vm) {
  if (!vm.start_date || !vm.end_date) return;

  if (vm.end_date < vm.start_date) {
    vm.errors.end_date = 'Event end date must be greater than the start date';
  }

  if (vm.start_date === vm.end_date && vm.start_time && vm.end_time && vm.end_time <= vm.start_time) {
    vm.errors.end_time = 'Event end time must be greater than the start time';
  }
}

function get_message(date_val, time_str) {
  if (!date_val) return null;
  const d = new Date(date_val);
  const [hours, minutes] = (time_str || "00:00").split(':').map(Number);
  d.setHours(hours, minutes, 0, 0);
  return d.getTime();
}

function validate_rsvp_range(vm, rsvp_start, rsvp_end) {
  if (rsvp_start && rsvp_end && rsvp_end <= rsvp_start) {
    if (vm.rsvp_end_date && vm.rsvp_start_date && vm.rsvp_end_date < vm.rsvp_start_date) {
      Reflect.set(vm.errors, "rsvp_end_date", "RSVP end date must be after RSVP start date");
    } else {
      Reflect.set(vm.errors, "rsvp_end_time", "RSVP end time must be after RSVP start time");
    }
    return false;
  }
  return true;
}


function validate_event_alignment(vm, rsvp_start, rsvp_end, event_start) {
  let is_valid = true;
  if (rsvp_start && event_start && rsvp_start > event_start) {
    if (vm.rsvp_start_date && vm.start_date && vm.rsvp_start_date > vm.start_date) {
      Reflect.set(vm.errors, "rsvp_start_date", "RSVP start date cannot be after event start date");
    } else {
      Reflect.set(vm.errors, "rsvp_start_time", "RSVP start time cannot be after event start time");
    }
    is_valid = false;
  }
  if (rsvp_end && event_start && rsvp_end > event_start) {
    if (vm.rsvp_end_date && vm.start_date && vm.rsvp_end_date > vm.start_date) {
      Reflect.set(vm.errors, "rsvp_end_date", "RSVP end date cannot be after event start date");
    } else {
      Reflect.set(vm.errors, "rsvp_end_time", "RSVP end time cannot be after event start time");
    }
    is_valid = false;
  }
  return is_valid;
}


export function validate_rsvp_dates(vm) {
  vm.errors = vm.errors || {};
  ['rsvp_start_date', 'rsvp_end_date', 'rsvp_start_time', 'rsvp_end_time'].forEach((k) => {
    Reflect.set(vm.errors, k, null);
  });

  const has_any_rsvp_value = !!(
    vm.rsvp_start_date ||
    vm.rsvp_start_time ||
    vm.rsvp_end_date ||
    vm.rsvp_end_time
  );

  if (!has_any_rsvp_value) return true;

  const is_partial_rsvp_window = !(
    vm.rsvp_start_date &&
    vm.rsvp_start_time &&
    vm.rsvp_end_date &&
    vm.rsvp_end_time
  );

  if (is_partial_rsvp_window) {
    if (!vm.rsvp_start_date) {
      Reflect.set(vm.errors, "rsvp_start_date", "RSVP start date is required");
    }
    if (!vm.rsvp_start_time) {
      Reflect.set(vm.errors, "rsvp_start_time", "RSVP start time is required");
    }
    if (!vm.rsvp_end_date) {
      Reflect.set(vm.errors, "rsvp_end_date", "RSVP end date is required");
    }
    if (!vm.rsvp_end_time) {
      Reflect.set(vm.errors, "rsvp_end_time", "RSVP end time is required");
    }
    return false;
  }

  const rsvp_start = get_message(vm.rsvp_start_date, vm.rsvp_start_time);
  const rsvp_end = get_message(vm.rsvp_end_date, vm.rsvp_end_time);
  const event_start = get_message(vm.start_date, vm.start_time);

  const range_ok = validate_rsvp_range(vm, rsvp_start, rsvp_end);
  const alignment_ok = validate_event_alignment(vm, rsvp_start, rsvp_end, event_start);

  return range_ok && alignment_ok;
}


export function validate_dates(vm) {
  vm.errors = vm.errors || {};
  const keys = ['start_date', 'end_date', 'start_time', 'end_time', 'rsvp_start_date', 'rsvp_end_date'];
  keys.forEach(k => Reflect.set(vm.errors, k, null));

  check_duration(vm);

  const hasDateErrors = keys.some(k => {
    const v = Reflect.get(vm.errors, k);
    return v !== null && v !== undefined && v !== '';
  });

  return !hasDateErrors;
}

export function validate_tickets(vm) {
  vm.errors = vm.errors || {};
  Object.keys(vm.errors).forEach((k) => {
    if (String(k).startsWith('ticket_')) {
      Reflect.set(vm.errors, k, null);
    }
  });
  let anyError = false;
  // First pass: initialize per-ticket keys, run basic validations and collect names
  const nameMap = {}; // normalized name -> array of indices
  (vm.tickets || []).forEach((ticket, idx) => {
    const idKey = idx;
    const startKey = `ticket_${idKey}_start_date`;
    const endKey = `ticket_${idKey}_end_date`;
    const nameKey = `ticket_${idKey}_name`;
    const priceKey = `ticket_${idKey}_price`;
    const qtyKey = `ticket_${idKey}_quantity`;

    Reflect.set(vm.errors, startKey, null);
    Reflect.set(vm.errors, endKey, null);
    Reflect.set(vm.errors, nameKey, null);
    Reflect.set(vm.errors, priceKey, null);
    Reflect.set(vm.errors, qtyKey, null);

    const nameVal = ticket.name ? String(ticket.name).trim() : '';
    const normalized = nameVal.toLowerCase();
    if (nameVal === '') {
      Reflect.set(vm.errors, nameKey, 'Ticket name is required');
      anyError = true;
    } else {
      if (!Reflect.get(nameMap, normalized)) Reflect.set(nameMap, normalized, []);
      Reflect.get(nameMap, normalized).push(idx);
    }

    if (
      ticket.price === undefined ||
      ticket.price === null ||
      Number(ticket.price) < 0
    ) {
      Reflect.set(vm.errors, priceKey, "Ticket price must be >= 0");
      anyError = true;
    }
    if (
      ticket.quantity === undefined ||
      ticket.quantity === null ||
      Number(ticket.quantity) <= 0
    ) {
      Reflect.set(vm.errors, qtyKey, "Ticket quantity must be greater than 0");
      anyError = true;
    }
    if (!ticket.start_date || String(ticket.start_date).trim() === "") {
      Reflect.set(vm.errors, startKey, "Ticket start date is required");
      anyError = true;
    }
    if (!ticket.end_date || String(ticket.end_date).trim() === "") {
      Reflect.set(vm.errors, endKey, "Ticket end date is required");
      anyError = true;
    }
    if (ticket.start_date && ticket.end_date) {
      if (ticket.end_date < ticket.start_date) {
        Reflect.set(vm.errors, endKey, 'Ticket end date must be after ticket start date');
        anyError = true;
      }
    }

    if (ticket.end_date) {
      const now = new Date();
      const ticketEnd = new Date(ticket.end_date);
      if (ticketEnd < now) {
        Reflect.set(vm.errors, endKey, 'Ticket end date cannot be in the past');
        anyError = true;
      }
    }

   if (vm.start_date && ticket.start_date) {
      const eventStart = new Date(`${vm.start_date}T${vm.start_time}`);
      const ticketStart = new Date(ticket.start_date);

      if (ticketStart > eventStart) {
        Reflect.set(vm.errors, startKey, 'Ticket start date cannot be after event start date');
        anyError = true;
      }
    }

    if (vm.end_date && ticket.end_date) {
      const eventStart = new Date(`${vm.start_date}T${vm.start_time}`);
      const ticketEnd = new Date(ticket.end_date);
      if (ticketEnd > eventStart) {
        Reflect.set(vm.errors, endKey, 'Ticket end date cannot be after event start date');
        anyError = true;
      }
      }
  });

  // Second pass: detect duplicate non-empty names and mark errors
  Object.keys(nameMap).forEach((normalized) => {
    const indices = Reflect.get(nameMap, normalized);
    if (indices.length > 1) {
      indices.forEach((idx) => {
        const nameKey = `ticket_${idx}_name`;
        Reflect.set(vm.errors, nameKey, 'Ticket name must be unique');
        anyError = true;
      });
    }
  });

  return !anyError;
}

export function validate_all(vm) {
  const datesOk = validate_dates(vm);
  let rsvpOk = true;
  let ticketsOk = true;
  if(vm.is_free){
    vm.errors = vm.errors || {};
    Object.keys(vm.errors).forEach((k) => {
      if (String(k).startsWith('ticket_')) {
        Reflect.set(vm.errors, k, null);
      }
    });
    rsvpOk = validate_rsvp_dates(vm);
  }
  else{
    // If the event is paid, clear stale RSVP errors.
    vm.errors = vm.errors || {};
    ['rsvp_start_date', 'rsvp_end_date', 'rsvp_start_time', 'rsvp_end_time'].forEach((k) => {
      if (Object.prototype.hasOwnProperty.call(vm.errors, k)) Reflect.set(vm.errors, k, null);
    });
    ticketsOk = validate_tickets(vm);
  }
  vm.date_validation_passed = datesOk && rsvpOk && ticketsOk;
  return vm.date_validation_passed;
}

export function get_filtered_tickets(vm, query) {
  if (!query) return vm.all_tickets;
  const q = String(query).toLowerCase().trim();
  return vm.all_tickets.filter(t => t.name.toLowerCase().includes(q));
}

export function on_file_change(vm, event) {
  const file = event.target.files[0];
  if (file) {
    vm.cover_image = file;
    vm.cover_image_preview = URL.createObjectURL(file);
  }
}

export function go_to_event(vm) {
  const event_id = vm.event_id || vm.event?.id || vm.$route.params.event_id;
  vm.$router.push({ name: 'view_event', params: { event_id } });
}



export function setup_autocomplete(vm, input) {
  vm.map_auto_complete = new window.google.maps.places.Autocomplete(input);
  vm.map_auto_complete_input = input;
  vm.map_auto_complete.addListener('place_changed', () => {
    const place = vm.map_auto_complete.getPlace();
    if (!place?.geometry?.location) return;

    vm.location = input.value;
    vm.latitude = place.geometry.location.lat();
    vm.longitude = place.geometry.location.lng();
  });
}

export function new_format_date(dateString) {
       if (!dateString) return 'N/A';
       const options = {
         year: 'numeric',
         month: 'short',
         day: 'numeric',
         hour: '2-digit',
         minute: '2-digit'
       };
       return new Date(dateString).toLocaleString(undefined, options);
}

export function add_extra(vm) {
  vm.show_add_extras = true;
  vm.show_extras_form = true;
  vm.extra_forms.push({
    selectedExtra: "",
    items: [
      { item_name: '', item_description: '', item_price: '' }
    ]
  });
}

export function add_extra_item(vm, extra_index) {
  const extra_form = vm.extra_forms.at(extra_index);
  if (extra_form) {
    const new_item = {
      item_name: '',
      item_description: '',
      item_price: 0
    };
    if (validate_extra_item(vm, new_item)) {
      extra_form.items.push(new_item);
    }
  }
}

export function validate_extra_item(vm, item) {
  return (
    typeof item === 'object' &&
    item !== null &&
    typeof item.item_name === 'string' &&
    typeof item.item_description === 'string' &&
    typeof item.item_price === 'number' &&
    item.item_price >= 0
  );
}

export function remove_extra(vm, extra_index) {
  vm.extra_forms.splice(extra_index, 1);
}

export function remove_extra_item(vm, extra_index, item_index) {
  const extra_form = vm.extra_forms.at(extra_index);
  if (extra_form && Array.isArray(extra_form.items)) {
    if (item_index >= 0 && item_index < extra_form.items.length) {
      extra_form.items.splice(item_index, 1);
    }
  }
}

export function back(vm){
  vm.$router.push({ name: "view_events" })
}

export function remove_ticket(vm, i) {
  vm.tickets.splice(i, 1)
  vm.validate_all && vm.validate_all();
}
// Default export: an object whose methods can be spread directly into a Vue
// component's `methods` object. These wrappers call the named exports with
// `this` bound as the first argument so the implementations can continue to
// operate on the component instance.
export default {
  new_format_date(dateString) { return new_format_date(dateString); },
  add_ticket() { return add_ticket(this); },
  back() { return back(this); },
  remove_ticket(i) { return remove_ticket(this, i); },
  validate_dates() { return validate_dates(this); },
  validate_rsvp_dates() { return validate_rsvp_dates(this); },
  validate_tickets() { return validate_tickets(this); },
  validate_all() { return validate_all(this); },
  get_filtered_tickets(query) { return get_filtered_tickets(this, query); },
  on_file_change(event) { return on_file_change(this, event); },
  go_to_event() { return go_to_event(this); },
  setup_autocomplete(input) { return setup_autocomplete(this, input); },
  add_extra() { return add_extra(this); },
  add_extra_item(extra_index) { return add_extra_item(this, extra_index); },
  validate_extra_item(item) { return validate_extra_item(this, item); },
  remove_extra(extra_index) { return remove_extra(this, extra_index); },
  remove_extra_item(extra_index, item_index) { return remove_extra_item(this, extra_index, item_index); },
};
